import PaymentCategories from '../../enums/PaymentCategories';
import ReCaptchaTypes from '../../enums/ReCaptchaTypes';
import {
  addDeliveryAddress,
  overwriteBillingAddress,
  overwriteDeliveryAddress,
  setBillingAddressCountries,
  setDeliveryAddressCountries,
  selectDeliveryAddress,
} from '../actions/address-actions';
import {switchToPudo} from '../actions/delivery-section-actions';
import {setLang} from '../actions/lang-actions';
import {updateOrderSummary} from '../actions/orderSummaryActions';
import {setPaymentOptionIndex, setPaymentOptions} from '../actions/paymentActions/payment-actions';
import changeSelectedPaymentCategory from '../actions/paymentActions/paymentCategorySelectionActions';
import setOptionalPaymentDetailsConsentConfiguration from '../actions/paymentActions/setOptionalPaymentDetailsConsentConfiguration';
import {setRecaptchaStrategy} from '../actions/recaptchaActions';
import {setSiteValues} from '../actions/site-actions';
import {setOrderComplete, setSocialSharing, updateStart} from '../actions/start-actions';
import {screenResized} from '../actions/uiActions';
import {
  getDeliveryAddressListData,
  getSelectedDeliveryAddressId,
  getSelectedDeliveryAddressCategory,
} from '../components/address/getDeliveryAddressListData';
import Language from '../lang/language';
import BillingCountries from '../utils/CountryCodes/BillingCountries';

import flattenObject from '../utils/flatten-object';
import {countryCurrencyNumberFormat} from '../utils/NumberFormat';

const dispatchSiteActions = (store, siteObj) => {
  store.dispatch(setSiteValues({
    isPostcodeLookupEnabled: !siteObj.postcodeLookupDisabled,
    isShowAutoRenewSubscriptionWarning: siteObj.isShowAutoRenewSubscriptionWarning,
    siteCode: siteObj.siteCode,
    paymentMethodsRequiringContactNumber: siteObj.paymentMethodsRequiringContactNumber,
    isContactNumberThresholdExceeded: siteObj.isContactNumberThresholdExceeded,
    showCaLeadWarning: siteObj.showCaLeadWarning,
    userLocation: siteObj.userLocation,
  }));
};

const dispatchLangActions = (store, startObj, siteObj) => {
  let siteObjectLang = {};
  let startObjLang = {};
  if (siteObj) {
    siteObjectLang = flattenObject(siteObj.lang);
  }
  if (startObj) {
    startObjLang = startObj.translations;
  }
  const mergedLang = {...siteObjectLang, ...startObjLang};
  store.dispatch(setLang(mergedLang));
};

export const transformBillingCountries = (store, defaultCountry) => {
  const transformedAndSortedCountries = BillingCountries
    .map(countryCode => ({
      code: countryCode,
      name: Language.getCountryName(countryCode),
    }))
    .sort((a, b) => (a.name < b.name ? -1 : 1));
  
  const initialValues = {
    country: defaultCountry,
  };
  
  return {
    countries: transformedAndSortedCountries,
    values: initialValues,
  };
};

export const dispatchBillingAddressActions = (store, defaultCountry) => {
  const {countries, values} = transformBillingCountries(store, defaultCountry);
  store.dispatch(setBillingAddressCountries(countries));
  store.dispatch(overwriteBillingAddress(values));
};

const dispatchDeliveryAddressSectionActions = (store, addressArray) => {
  const savedAddresses = addressArray.slice();
  const newAddress = savedAddresses.pop();
  const addressId = getSelectedDeliveryAddressId();
  
  savedAddresses.forEach((address) => {
    store.dispatch(addDeliveryAddress(address.addressId, address.initialValues, 'STORED_RESIDENTIAL_ADDRESS'));
  });
  
  store.dispatch(overwriteDeliveryAddress(newAddress.addressId, newAddress.initialValues, 'NEW_RESIDENTIAL_ADDRESS'));
  store.dispatch(setDeliveryAddressCountries(addressArray[addressArray.length - 1].countries));
  store.dispatch(selectDeliveryAddress(addressId, getSelectedDeliveryAddressCategory(addressId)));
};

const dispatchPaymentOptionActions = (store, paymentOptions) => {
  store.dispatch(setPaymentOptions(paymentOptions.paymentOptions.map((paymentOption, index) => ({
    ...paymentOption,
    apiIndex: index,
  }))));
  
  if (!window?.start?.settings?.should_defer_payment_option_preselection) {
    store.dispatch(setPaymentOptionIndex(paymentOptions.selectedIndex));
  }
  
  store.dispatch(changeSelectedPaymentCategory(PaymentCategories[paymentOptions.paymentMethodCategory]));
  store.dispatch(setOptionalPaymentDetailsConsentConfiguration(window.start.optionalConsentConfiguration));
};

const toPriceString = (locale, priceObj) => {
  if (!priceObj) {
    return countryCurrencyNumberFormat(locale,
      'GBP',
      0);
  } else {
    return countryCurrencyNumberFormat(locale,
      priceObj.currencyCode,
      priceObj.value);
  }
}

const dispatchOrderSummaryFromStartObject = (store) => {
  if (window.start.orderSummary) {
    store.dispatch(updateOrderSummary(window.start.orderSummary));
    return;
  }
  
  const {
    order, displayOrderTotal, orderTotal, credit, siteDataV2,
  } = window.start;
  const locale = siteDataV2 && siteDataV2.locale && siteDataV2.locale.replace('_', '-');
  
  let rewardPoints = {};
  let creditInfo = {};
  let orderInfo = {};
  const orderTotalInfo = {orderTotal: displayOrderTotal};
  
  if (order) {
    if (window.order) {
      orderInfo = {
        ...window.order,
        itemCount: order.basketItemCount,
        surchargeExists: !!order.paypalSurcharge.value,
        cv2Required: order.cv2Required,
        orderTotalNumeric: orderTotal.value,
        totalDiscountNumeric: order.totalDiscountExcludingFreeGifts.value,
        discounts: order.discounts,
      };
    } else {
      orderInfo = {
        itemTotalPrice: toPriceString(locale, order.totalPriceOfItems),
        deliveryPrice: toPriceString(locale, order.totalShippingPrice),
        promotionsTotalPrice: toPriceString(locale, order.totalDiscountExcludingFreeGifts),
        paypalSurcharge: toPriceString(locale, order.paypalSurcharge),
        itemCount: order.basketItemCount,
        surchargeExists: !!order.paypalSurcharge?.value,
        cv2Required: order.cv2Required,
        orderTotalNumeric: orderTotal.value,
        totalDiscountNumeric: order.totalDiscountExcludingFreeGifts?.value,
        discounts: order.discounts,
      };
      
      if (order.rewardPoints) {
        rewardPoints = {
          rewardPointsCollected: order.rewardPoints.collected,
          rewardPointsSpent: order.rewardPoints.spent,
          rewardPointsSpentMoreThanZero: order.rewardPoints.spent > 0,
          rewardPointsSpentAmount: order.rewardPoints.spent,
        };
      }
    }
  }
  
  if (credit) {
    creditInfo = {
      usedCredit: credit.usedCredit,
      applicableCredit: credit.applicableCredit,
      applicableCreditNumeric: credit.applicableCreditNumeric,
      creditBalance: credit.creditBalance,
      totalCreditBalance: credit.totalCreditBalance,
    };
  }
  
  store.dispatch(updateOrderSummary({
    ...orderInfo, ...creditInfo, ...rewardPoints, ...orderTotalInfo,
  }));
};

const initializeStoreFromDom = (store) => {
  if (window.siteObj) {
    dispatchSiteActions(store, window.siteObj);
  }
  
  const start = window.start || {};
  
  store.dispatch(updateStart(start));
  
  dispatchOrderSummaryFromStartObject(store);
  
  dispatchLangActions(store, window.start, window.siteObj);
  
  const deliveryAddressListData = getDeliveryAddressListData();
  if (deliveryAddressListData) {
    dispatchDeliveryAddressSectionActions(store, deliveryAddressListData);
  }
  
  const startObj = window.start || {};
  
  if (startObj.addresses) {
    dispatchBillingAddressActions(store, window.start.addresses.defaultDeliveryCountryCode);
  } else if (startObj.deliveryCountries) {
    dispatchBillingAddressActions(store, window.start.deliveryAddress.countryCode);
  }
  
  if (startObj.paymentOptions) {
    dispatchPaymentOptionActions(store, startObj.paymentOptions);
  }
  
  store.dispatch(setSocialSharing(startObj.socialSharingChannels));
  
  if (window.orderCompleteObj) {
    store.dispatch(setOrderComplete({...window.orderCompleteObj, ...window.siteObj}));
  }
  
  if (window.acceptedCardTypes) {
    store.dispatch(updateStart({acceptedCardTypes: window.acceptedCardTypes.split(',')}));
  }
  
  const {orderNumber} = window.start;

  if (window.sessionStorage.getItem(`deliveryType-${orderNumber}`) === 'pudo') {
    store.dispatch(switchToPudo());
  }
  
  if (startObj.reCaptchaStrategy
    && (startObj.reCaptchaStrategy === ReCaptchaTypes.RECAPTCHA_V2_INVISIBLE
      || startObj.reCaptchaStrategy === ReCaptchaTypes.RECAPTCHA_V2_VISIBLE
      || startObj.reCaptchaStrategy === ReCaptchaTypes.DISABLED)) {
    store.dispatch(setRecaptchaStrategy(startObj.reCaptchaStrategy));
  }
};

const initializeState = (store) => {
  initializeStoreFromDom(store);
  window.addEventListener('resize', () => {
    store.dispatch(screenResized(window.innerWidth, window.innerHeight));
  });
};

export default initializeState;
